import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, Sun, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import GradientLoginLayout from '@/components/GradientLoginLayout';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [school, setSchool] = useState(null);
  
  const { login, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Get school from URL param or localStorage
  const schoolFromUrl = searchParams.get('school');
  const schoolCode = schoolFromUrl || localStorage.getItem('school_code') || null;
  
  // Fetch school info if school param exists
  useEffect(() => {
    if (schoolCode) {
      localStorage.setItem('school_code', schoolCode);
      localStorage.setItem('login_source', `/${schoolCode}`);
      fetchSchoolInfo();
    }
  }, [schoolCode]);

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools/public/${schoolCode}`);
      setSchool(response.data);
    } catch (error) {
      console.error('Failed to fetch school:', error);
    }
  };

  useEffect(() => {
    if (user) {
      navigateToDashboard(user.role);
    }
  }, [user]);

  const navigateToDashboard = (role) => {
    if (role === 'parent') {
      navigate('/parent');
    } else {
      toast.error('Invalid credentials for Parent Portal');
      navigate('/auth');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Pass school_code and expected_role for validation
      const userData = await login(email, password, schoolCode, 'parent');
      if (userData.role !== 'parent') {
        toast.error('This portal is for Parents only. Please use the correct portal.');
        return;
      }
      navigateToDashboard(userData.role);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GradientLoginLayout portalType="parent">
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4"
      >
        {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm" data-testid="parent-login-card">
          <CardHeader className="text-center">
            {school && (
              <div className="flex flex-col items-center mb-4">
                {(school.logo_url || school.logo) && (
                  <img src={school.logo_url || school.logo} alt={school.name} className="w-16 h-16 object-contain mb-2 rounded-lg" />
                )}
                <p className="text-sm font-medium text-primary">{school.name}</p>
              </div>
            )}
            <CardTitle className="text-2xl">Parent Login</CardTitle>
            <CardDescription>Use your Parent ID or Email to access your child's information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Parent ID / Email</Label>
                <Input
                  id="email"
                  type="text"
                  placeholder="P_ADM2024001 or parent@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="parent-email-input"
                  className="bg-white dark:bg-slate-900"
                />
                <p className="text-xs text-muted-foreground">Enter P_[Child's Admission No.] or registered Email</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="parent-password-input"
                    className="bg-white dark:bg-slate-900 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" 
                disabled={loading}
                data-testid="parent-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/auth" 
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Portal Selection
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Need help? Contact your school administration
        </p>
      </motion.div>
    </GradientLoginLayout>
  );
};

export default ParentLoginPage;
